import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CalendarIcon, CurrencyDollarIcon, PhoneIcon, XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { statusLabels } from '../config/statusColors';

const StatusChangeModal = ({ isOpen, onClose, currentStatus, onConfirm }) => {
  const modalRef = useRef(null);
  const activeInputRef = useRef(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [additionalMemo, setAdditionalMemo] = useState('');
  const [internalMemo, setInternalMemo] = useState('');
  const [showInternalMemo, setShowInternalMemo] = useState(false);
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDetail, setCancelDetail] = useState('');
  const [contractAmount, setContractAmount] = useState('');
  const [skipContractDoc, setSkipContractDoc] = useState(true);
  const [contractFiles, setContractFiles] = useState([]);

  // 모달이 열릴 때와 키보드 포커스 시 스크롤 조정
  useEffect(() => {
    if (isOpen) {
      // 모달이 열릴 때 body 스크롤 방지
      document.body.style.overflow = 'hidden';
      
      // 모바일 키보드 대응
      const handleFocus = (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          activeInputRef.current = e.target;
          setTimeout(() => {
            if (modalRef.current && activeInputRef.current) {
              const inputRect = activeInputRef.current.getBoundingClientRect();
              
              // 입력 필드가 화면 하단에 있으면 스크롤
              if (inputRect.bottom > window.innerHeight * 0.5) {
                activeInputRef.current.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center'
                });
              }
            }
          }, 300); // 키보드가 올라오는 시간 대기
        }
      };

      document.addEventListener('focusin', handleFocus);
      
      return () => {
        document.removeEventListener('focusin', handleFocus);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // 상태별 옵션 정의
  const getStatusOptions = () => {
    if (currentStatus === 'received') {
      return [
        { 
          id: 'visit_scheduled', 
          icon: CalendarIcon, 
          label: '방문 예약 완료',
          message: '고객과 방문 상담 일정을 잡았습니다.',
          color: 'bg-blue-100 text-blue-700 hover:bg-blue-200'
        },
        { 
          id: 'quote_review', 
          icon: CurrencyDollarIcon, 
          label: '견적 검토 중',
          message: '고객이 견적을 검토하고 있습니다.',
          color: 'bg-green-100 text-green-700 hover:bg-green-200'
        },
        { 
          id: 'simple_consultation', 
          icon: PhoneIcon, 
          label: '단순 상담',
          message: '정보 안내만 진행했습니다.',
          color: 'bg-green-100 text-green-700 hover:bg-green-200'
        },
        { 
          id: 'immediate_cancel', 
          icon: XCircleIcon, 
          label: '즉시 취소',
          message: '프로젝트가 취소되었습니다.',
          color: 'bg-red-500 text-white hover:bg-red-600',
          isCancel: true
        }
      ];
    }
    return [];
  };

  const options = getStatusOptions();

  const handleCancelProcess = () => {
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    if (!cancelReason) {
      alert('취소 사유를 선택해주세요.');
      return;
    }

    const cancelReasonText = {
      'customer_change': '고객 변심',
      'budget_issue': '예산 부족',
      'schedule_issue': '일정/시기 문제',
      'competitor': '타사 선택',
      'other': '기타'
    }[cancelReason] || '기타';

    const statusData = {
      status: 'cancelled',
      status_log_type: 'review_cancel',
      status_log_message: `취소 사유: ${cancelReasonText}`,
      cancel_reason: `${cancelReasonText}${cancelDetail ? ` - ${cancelDetail}` : ''}`,
      status_internal_memo: internalMemo || null,
      status_is_public: true  // log_message는 항상 공개, internal_memo만 관리자/소장에게만 표시
    };

    if (cancelDetail) {
      statusData.status_log_message += ` (${cancelDetail})`;
    }

    onConfirm(statusData);
    
    // 초기화
    setShowCancelConfirm(false);
    setShowCancelReason(false);
    setCancelReason('');
    setCancelDetail('');
    setInternalMemo('');
    setShowInternalMemo(false);
  };

  const handleConfirm = () => {
    // 검토 → 계약 전환 시
    if (currentStatus === 'review') {
      if (!contractAmount) {
        alert('계약금액을 입력해주세요.');
        return;
      }
      
      const statusData = {
        status: 'contract',
        contract_amount: contractAmount.replace(/,/g, ''),
        status_log_type: 'contract_signed',
        status_log_message: `계약이 체결되었습니다. (계약금액: ${contractAmount}원)`,
        status_user_memo: additionalMemo || null,
        status_internal_memo: showInternalMemo ? internalMemo : null,
        status_is_public: true  // log_message는 항상 공개, internal_memo만 관리자/소장에게만 표시
      };
      
      // 계약서 파일이 있으면 함께 전달
      if (!skipContractDoc && contractFiles.length > 0) {
        statusData.contractFiles = contractFiles;
      }
      
      onConfirm(statusData);
      // 초기화
      setContractAmount('');
      setSkipContractDoc(true);
      setContractFiles([]);
      setAdditionalMemo('');
      return;
    }
    
    // 계약 → 완료 전환 시
    if (currentStatus === 'contract') {
      const statusData = {
        status: 'completed',
        status_log_type: 'project_completed',
        status_log_message: '프로젝트가 완료되었습니다.',
        status_user_memo: additionalMemo || null,
        status_internal_memo: showInternalMemo ? internalMemo : null,
        status_is_public: true  // log_message는 항상 공개, internal_memo만 관리자/소장에게만 표시
      };
      
      onConfirm(statusData);
      // 초기화
      setAdditionalMemo('');
      setInternalMemo('');
      setShowInternalMemo(false);
      return;
    }
    
    const selected = options.find(opt => opt.id === selectedOption);
    if (!selected) return;

    // 즉시 취소인 경우
    if (selected.isCancel) {
      if (!cancelReason) {
        alert('취소 사유를 선택해주세요.');
        return;
      }

      const cancelReasonText = {
        'customer_change': '고객 변심',
        'budget_issue': '예산 부족',
        'schedule_issue': '일정/시기 문제',
        'competitor': '타사 선택',
        'other': '기타'
      }[cancelReason] || '기타';

      const statusData = {
        status: 'cancelled',
        status_log_type: 'immediate_cancel',
        status_log_message: `취소 사유: ${cancelReasonText}`,
        cancel_reason: `${cancelReasonText}${cancelDetail ? ` - ${cancelDetail}` : ''}`,
        status_internal_memo: internalMemo || null,
        status_is_public: true  // log_message는 항상 공개, internal_memo만 관리자/소장에게만 표시
      };

      if (cancelDetail) {
        statusData.status_log_message += ` (${cancelDetail})`;
      }

      onConfirm(statusData);
    } else {
      // 일반 상태 변경
      const statusData = {
        status: getNewStatusValue(),
        status_log_type: selected.id,
        status_log_message: selected.message,
        status_user_memo: additionalMemo || null,
        status_internal_memo: showInternalMemo ? internalMemo : null,
        status_is_public: true  // log_message는 항상 공개, internal_memo만 관리자/소장에게만 표시
      };

      onConfirm(statusData);
    }
    
    // 초기화
    setSelectedOption('');
    setAdditionalMemo('');
    setInternalMemo('');
    setShowInternalMemo(false);
    setShowCancelReason(false);
    setCancelReason('');
    setCancelDetail('');
    setContractAmount('');
    setSkipContractDoc(true);
    setContractFiles([]);
  };

  const getNewStatusLabel = () => {
    if (currentStatus === 'received') return '검토';
    if (currentStatus === 'review') return '계약';
    if (currentStatus === 'contract') return '완료';
    return '';
  };

  const getNewStatusValue = () => {
    if (currentStatus === 'received') return 'review';
    if (currentStatus === 'review') return 'contract';
    if (currentStatus === 'contract') return 'completed';
    return '';
  };
  
  const formatNumber = (value) => {
    // 숫자만 추출
    const number = value.replace(/[^0-9]/g, '');
    // 천단위 콤마 추가
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // WebView 감지 (안드로이드 앱)
  const isWebView = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /wv/.test(userAgent) || (window.Android && typeof window.Android === 'object');
  };

  const isApp = isWebView();

  return (
    <>
      {/* 백드롭 - 살짝 투명하게 해서 뒤가 보이도록 */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* 모달 - 고정 크기 플로팅 카드 */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none p-4">
        <div 
          ref={modalRef}
          className={`
            pointer-events-auto
            bg-white 
            rounded-2xl 
            shadow-2xl 
            flex flex-col
            transition-opacity duration-300 ease-out
            
            /* 모바일: 고정 크기 - 상하단 메뉴 보임 */
            w-[calc(100%-20px)]
            max-w-[calc(100%-20px)]
            ${isApp ? 'h-[calc(100vh-100px)]' : 'h-[calc(100vh-120px)]'}
            
            /* PC: 중앙 정렬 with 고정 크기 */
            sm:max-w-lg
            sm:w-full
            sm:h-auto
            sm:max-h-[75vh]
            
            /* 위치 조정 */
            ${isApp ? 'mt-[50px] mb-[50px]' : 'mt-[60px] mb-[60px]'}
            sm:mt-0
            sm:mb-0
          `}
        >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            상태를 {statusLabels[getNewStatusLabel()]}(으)로 변경
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* 상태 진행 표시바 */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {['received', 'review', 'contract', 'completed'].map((status, index, array) => {
                const statuses = ['received', 'review', 'contract', 'completed'];
                const currentIndex = statuses.indexOf(currentStatus);
                const isCompleted = index <= currentIndex;
                const isCurrent = index === currentIndex;
                
                return (
                  <React.Fragment key={status}>
                    <div className="flex flex-col items-center">
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                        ${isCompleted ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}
                        ${isCurrent ? 'ring-2 ring-blue-300' : ''}
                      `}>
                        {isCompleted && index < currentIndex ? (
                          <CheckCircleIcon className="h-4 w-4" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span className={`mt-1 text-xs ${isCurrent ? 'font-bold text-gray-900' : 'text-gray-500'}`}>
                        {statusLabels[status]}
                      </span>
                    </div>
                    {index < array.length - 1 && (
                      <div className="flex-1 h-0.5 mx-1 -mt-4">
                        <div className={`h-full ${
                          index < currentIndex ? 'bg-blue-500' : 'bg-gray-300'
                        }`}></div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {currentStatus === 'received' && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                📞 고객 상담을 어떻게 진행하셨나요?
              </p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {options.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.id}
                      onClick={() => {
                        setSelectedOption(option.id);
                        setShowCancelReason(option.isCancel || false);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        selectedOption === option.id
                          ? option.isCancel 
                            ? 'border-red-500 bg-red-50'
                            : 'border-blue-500 bg-blue-50'
                          : option.isCancel
                            ? 'border-red-200 hover:border-red-300 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`h-8 w-8 mx-auto mb-2 ${
                        selectedOption === option.id 
                          ? option.isCancel ? 'text-red-600' : 'text-blue-600' 
                          : option.isCancel ? 'text-red-400' : 'text-gray-400'
                      }`} />
                      <span className={`text-sm font-medium ${
                        selectedOption === option.id 
                          ? option.isCancel ? 'text-red-700' : 'text-blue-700' 
                          : option.isCancel ? 'text-red-600' : 'text-gray-700'
                      }`}>
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {selectedOption && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    선택하신 항목: <span className="font-medium">{options.find(o => o.id === selectedOption)?.label}</span>
                  </p>
                </div>
              )}

              {/* 취소 사유 선택 */}
              {showCancelReason && (
                <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800 mb-3">취소 사유를 선택해주세요 (필수)</p>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="customer_change"
                        checked={cancelReason === 'customer_change'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">고객 변심 (단순 변심)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="budget_issue"
                        checked={cancelReason === 'budget_issue'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">예산 부족</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="schedule_issue"
                        checked={cancelReason === 'schedule_issue'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">일정/시기 문제</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="competitor"
                        checked={cancelReason === 'competitor'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">타사 선택</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="other"
                        checked={cancelReason === 'other'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">기타</span>
                    </label>
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상세 내용 (선택사항)
                    </label>
                    <textarea
                      value={cancelDetail}
                      onChange={(e) => setCancelDetail(e.target.value)}
                      placeholder="취소 사유에 대한 구체적인 내용을 입력해주세요"
                      className="w-full px-3 py-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      rows="2"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {!showCancelReason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      추가로 기록하실 내용이 있나요? (선택사항)
                    </label>
                    <textarea
                      value={additionalMemo}
                      onChange={(e) => setAdditionalMemo(e.target.value)}
                      placeholder="예: 다음주 화요일 오전 10시 방문 약속"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      rows="2"
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }}
                    />
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="internal-memo"
                    checked={showInternalMemo}
                    onChange={(e) => setShowInternalMemo(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="internal-memo" className="ml-2 text-sm text-gray-600">
                    내부 메모로 작성 (고객에게 보이지 않음)
                  </label>
                </div>

                {showInternalMemo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      내부 메모 (관리자/소장만 확인 가능)
                    </label>
                    <textarea
                      value={internalMemo}
                      onChange={(e) => setInternalMemo(e.target.value)}
                      placeholder="예: 고객이 가격에 민감함, 타사 견적 비교 중"
                      className="w-full px-3 py-2 border border-orange-300 rounded-md focus:ring-orange-500 focus:border-orange-500 bg-orange-50"
                      rows="2"
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* 검토 → 계약/취소 */}
          {currentStatus === 'review' && !showCancelConfirm && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                💼 계약 정보를 입력해주세요
              </p>
              
              <div className="space-y-4">
                {/* 계약금액 입력 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    계약금액 (필수)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={contractAmount}
                      onChange={(e) => setContractAmount(formatNumber(e.target.value))}
                      placeholder="예: 5,000,000"
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      원
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    금액은 나중에 수정할 수 있습니다
                  </p>
                </div>
                
                {/* 계약서 업로드 옵션 */}
                <div className="space-y-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={skipContractDoc}
                        onChange={(e) => setSkipContractDoc(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        계약서는 나중에 업로드하겠습니다
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-2 ml-6">
                      계약 상태로 변경 후 계약 정보 섹션에서 업로드할 수 있습니다
                    </p>
                  </div>
                  
                  {/* 계약서 첨부 */}
                  {!skipContractDoc && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        계약서 첨부
                      </label>
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.hwp,.jpg,.jpeg,.png"
                        onChange={(e) => setContractFiles(Array.from(e.target.files))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        PDF, Word, HWP, 이미지 파일을 업로드할 수 있습니다
                      </p>
                      {contractFiles.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600">
                          선택된 파일: {contractFiles.map(f => f.name).join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* 추가 메모 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    계약 조건/메모 (선택사항)
                  </label>
                  <textarea
                    value={additionalMemo}
                    onChange={(e) => setAdditionalMemo(e.target.value)}
                    placeholder="예: 착공 예정일 2025년 2월 1일"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows="2"
                    onFocus={(e) => {
                      setTimeout(() => {
                        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }}
                  />
                </div>
                
                {/* 내부 메모 체크박스 */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="internal-memo-contract"
                    checked={showInternalMemo}
                    onChange={(e) => setShowInternalMemo(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="internal-memo-contract" className="ml-2 text-sm text-gray-600">
                    내부 메모로 작성 (고객에게 보이지 않음)
                  </label>
                </div>

                {showInternalMemo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      내부 메모 (관리자/소장만 확인 가능)
                    </label>
                    <textarea
                      value={internalMemo}
                      onChange={(e) => setInternalMemo(e.target.value)}
                      placeholder="예: 고객이 가격에 민감함, 추가 할인 협의 필요"
                      className="w-full px-3 py-2 border border-orange-300 rounded-md focus:ring-orange-500 focus:border-orange-500 bg-orange-50"
                      rows="2"
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }}
                    />
                  </div>
                )}
              </div>
            </>
          )}
          
          {/* 계약 → 완료 */}
          {currentStatus === 'contract' && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                ✅ 프로젝트를 완료 처리하시겠습니까?
              </p>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
                <p className="text-sm text-green-800 font-medium mb-2">
                  완료 처리 시 자동으로 처리되는 사항:
                </p>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• 보증서 발급 (보증 기간 시작)</li>
                  <li>• 완료 알림 발송</li>
                  <li>• VIP 고객 리워드 적립 (해당 시)</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    완료 메모 (선택사항)
                  </label>
                  <textarea
                    value={additionalMemo}
                    onChange={(e) => setAdditionalMemo(e.target.value)}
                    placeholder="예: 모든 공사가 성공적으로 완료되었습니다"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows="2"
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="internal-memo-completed"
                    checked={showInternalMemo}
                    onChange={(e) => setShowInternalMemo(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="internal-memo-completed" className="ml-2 text-sm text-gray-600">
                    내부 메모로 작성 (고객에게 보이지 않음)
                  </label>
                </div>

                {showInternalMemo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      내부 메모 (관리자/소장만 확인 가능)
                    </label>
                    <textarea
                      value={internalMemo}
                      onChange={(e) => setInternalMemo(e.target.value)}
                      placeholder="예: 고객 만족도 높음, 추가 작업 요청 가능성 있음"
                      className="w-full px-3 py-2 border border-orange-300 rounded-md focus:ring-orange-500 focus:border-orange-500 bg-orange-50"
                      rows="2"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* 취소 확인 화면 */}
          {currentStatus === 'review' && showCancelConfirm && (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <h4 className="text-red-800 font-semibold mb-3 flex items-center">
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  프로젝트를 취소하시겠습니까?
                </h4>
                <div className="space-y-2 text-sm text-red-700">
                  <p className="font-medium">⚠️ 취소 처리 시 다음과 같이 처리됩니다:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• 고객에게 취소 알림 발송</li>
                    <li>• 고객은 90일 동안 취소 내역 확인 가능</li>
                    <li>• 90일 후 고객 접속 차단</li>
                    <li>• 본사/소장은 계속 열람 가능</li>
                    <li>• 수수료 미발생</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    취소 사유 선택 (필수)
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="customer_change"
                        checked={cancelReason === 'customer_change'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">고객 변심</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="budget_issue"
                        checked={cancelReason === 'budget_issue'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">예산 문제</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="schedule_issue"
                        checked={cancelReason === 'schedule_issue'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">일정 조율 실패</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="competitor"
                        checked={cancelReason === 'competitor'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">타사 선택</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="cancelReason"
                        value="other"
                        checked={cancelReason === 'other'}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="mr-2 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">기타</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    상세 사유 (선택)
                  </label>
                  <textarea
                    value={cancelDetail}
                    onChange={(e) => setCancelDetail(e.target.value)}
                    placeholder="취소 사유에 대한 구체적인 내용을 입력해주세요"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                    rows="2"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="internal-memo-cancel"
                    checked={showInternalMemo}
                    onChange={(e) => setShowInternalMemo(e.target.checked)}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="internal-memo-cancel" className="ml-2 text-sm text-gray-600">
                    내부 메모로 작성 (고객에게 보이지 않음)
                  </label>
                </div>

                {showInternalMemo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      내부 메모 (관리자/소장만 확인 가능)
                    </label>
                    <textarea
                      value={internalMemo}
                      onChange={(e) => setInternalMemo(e.target.value)}
                      placeholder="예: 고객이 가격에 민감함, 타사 견적과 비교 중"
                      className="w-full px-3 py-2 border border-orange-300 rounded-md focus:ring-orange-500 focus:border-orange-500 bg-orange-50"
                      rows="2"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* 버튼 영역 - 하단 고정 */}
        <div className="flex gap-2 sm:gap-3 p-3 sm:p-4 border-t bg-gradient-to-b from-gray-50 to-white rounded-b-2xl flex-shrink-0">
          {currentStatus === 'review' && showCancelConfirm ? (
            <>
              <button
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelReason('');
                  setCancelDetail('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                돌아가기
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={!cancelReason}
                className="flex-1 px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                취소 확정
              </button>
            </>
          ) : currentStatus === 'review' ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                닫기
              </button>
              <button
                onClick={handleCancelProcess}
                className="px-4 py-2 text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700"
              >
                취소 처리
              </button>
              <button
                onClick={handleConfirm}
                disabled={!contractAmount}
                className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-1"
              >
                <span>계약 진행</span>
                <span className="text-lg">→</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleConfirm}
                disabled={
                  (currentStatus === 'received' && !selectedOption) ||
                  (currentStatus === 'contract' && false) // 계약 → 완료는 항상 가능
                }
                className={`flex-1 px-4 py-2 text-white rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed ${
                  showCancelReason 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {showCancelReason ? '취소하기' : `${getNewStatusLabel()}(으)로 변경`}
              </button>
            </>
          )}
        </div>
        </div>
      </div>
    </>
  );
};

export default StatusChangeModal;